import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { apiFetch, persistToken, clearToken, getToken, SESSION_EXPIRED } from "@/lib/api";

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

const EMERGENT_AUTH = "https://auth.emergentagent.com/";

function extractSessionId(url) {
  const m = url.match(/[?#&]session_id=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const processed = useRef(new Set());

  const exchangeSession = useCallback(async (sessionId) => {
    if (processed.current.has(sessionId)) return;
    processed.current.add(sessionId);
    try {
      const res = await apiFetch("/auth/session", {
        method: "POST",
        auth: false,
        body: { session_id: sessionId },
      });
      if (!res?.token || !res?.user) throw new Error("Invalid sign-in response");
      persistToken(res.token);
      setUser(res.user);
    } catch {
      setAuthError("Google sign-in could not be completed. Please try signing in again.");
    }
  }, []);

  const checkExisting = useCallback(async () => {
    setAuthError("");
    setLoading(true);
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await apiFetch("/auth/me");
      if (getToken() !== token) return null;
      setUser(me);
      return me;
    } catch (e) {
      if (getToken() !== token) return null;
      if (e.status === 401) { clearToken(); setUser(null); }
      else setAuthError("EARNOS could not verify your session. Check your connection and retry.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (typeof window !== "undefined") {
        const isAuthCallback = ["/", "/auth"].includes(window.location.pathname);
        let sid = null;
        try {
          sid = isAuthCallback ? extractSessionId(window.location.href) : null;
        } catch {
          setAuthError("This sign-in link is invalid. Please start sign-in again.");
          window.history.replaceState(window.history.state, "", window.location.pathname);
          setLoading(false);
          return;
        }
        if (sid) {
          await exchangeSession(sid);
          try {
            window.history.replaceState(window.history.state, "", window.location.pathname);
          } catch {}
          setLoading(false);
          return;
        }
      }
      await checkExisting();
    })();
  }, [exchangeSession, checkExisting]);

  useEffect(() => {
    const expired = () => { setUser(null); setAuthError(""); setLoading(false); };
    window.addEventListener(SESSION_EXPIRED, expired);
    return () => window.removeEventListener(SESSION_EXPIRED, expired);
  }, []);

  const register = async (email, password, firstName) => {
    const res = await apiFetch("/auth/register", {
      method: "POST",
      auth: false,
      body: { email, password, first_name: firstName },
    });
    persistToken(res.token);
    setUser(res.user);
    setAuthError("");
    return res.user;
  };

  const login = async (email, password) => {
    const res = await apiFetch("/auth/login", {
      method: "POST",
      auth: false,
      body: { email, password },
    });
    persistToken(res.token);
    setUser(res.user);
    setAuthError("");
    return res.user;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/auth?oauth_return=" + Date.now();
    window.location.href = `${EMERGENT_AUTH}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {}
    clearToken();
    setUser(null);
    setAuthError("");
  };

  const refresh = checkExisting;

  return (
    <AuthContext.Provider
      value={{ user, loading, authError, retrySession: checkExisting, register, login, loginWithGoogle, logout, refresh, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}
